// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// O DIA E A HORA EM PORTUGAL — a única formulação de «hoje» e de «agora»
// ============================================================================
//
// 🔴 Revisão geral, 2026-09-30: *«os travões J-0 calculam o agora em UTC»*. O `agoraNoPlano`
//    lia `getUTCHours()` e `getUTCDate()`, e `Europe/Lisbon` não aparecia em lado nenhum do
//    código. As horas do plano — o `in` e o `out` dos movimentos, a hora de recolha do pedido —
//    são **hora legal de Portugal**, escritas por quem planeia em Portugal. Compará-las com
//    minutos UTC deixava o travão das 12 h **uma hora mais permissivo** todo o horário de Verão,
//    e entre as 00:00 e as 01:00 de Lisboa o dia anterior ainda não contava como «já passou».
//
// 📌 **O referencial do plano é o dia de Portugal**, e não o do servidor nem o do navegador:
//    · o contentor corre em UTC, e a base do `dev` também (`TimeZone = Etc/UTC`, medido a
//      2026-09-30 com `SELECT current_setting('TimeZone')`);
//    · o navegador corre no fuso de quem está a olhar, que pode estar num hotel em Madrid.
//    Duas contas davam duas respostas à pergunta «isto já passou?», uma vez por dia, à
//    meia-noite — que é exactamente quando ninguém está a olhar.
//
// ⚠️ **`Intl.DateTimeFormat` e não uma soma de 60 minutos**: a mudança de hora (último domingo
//    de Março e de Outubro) não é aritmética que se queira reescrever à mão. É ECMAScript puro,
//    logo o domínio continua sem `node:*` e sem DOM.
// ============================================================================
import { indiceDaData, janelaDoAno } from './janela-do-mapa.js';
/** O fuso das horas do plano. Um só sítio — o SQL da API constrói-se a partir daqui. */
export const FUSO_DE_PORTUGAL = 'Europe/Lisbon';
/**
 * ⚠️ Criado uma vez: construir um `DateTimeFormat` custa ordens de grandeza mais do que
 * usá-lo, e o portão de escrita chama isto em cada gesto.
 *
 * ⚠️ `hourCycle: 'h23'` e não `hour12: false` — com o segundo, alguns motores escrevem a
 * meia-noite como «24», e a conta dos minutos passava a 1440.
 */
let formatador = null;
function partes(quando) {
    formatador ??= new Intl.DateTimeFormat('en-GB', {
        timeZone: FUSO_DE_PORTUGAL,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    });
    const p = {};
    for (const x of formatador.formatToParts(quando)) {
        if (x.type !== 'literal')
            p[x.type] = Number(x.value);
    }
    return {
        ano: p['year'] ?? NaN,
        mes: p['month'] ?? NaN,
        dia: p['day'] ?? NaN,
        minutos: (p['hour'] ?? NaN) * 60 + (p['minute'] ?? NaN),
    };
}
/** O dia civil e a hora em Portugal. O instante entra por parâmetro: um relógio escondido não se testa. */
export function diaEHoraEmPortugal(quando = new Date()) {
    return partes(quando);
}
/** `YYYY-MM-DD` do dia de hoje em Portugal. */
export function hojeEmPortugal(quando = new Date()) {
    const { ano, mes, dia } = partes(quando);
    return `${String(ano)}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}
/**
 * O índice do dia de hoje em Portugal no ano `ano` — contínuo, logo negativo para um ano futuro
 * e maior do que 365 para um passado. Quem precisar de `-1` fora do ano (um marcador de ecrã)
 * corta-o do seu lado.
 *
 * 📌 A aritmética de calendário continua em UTC (`Date.UTC`), e é de propósito: aqui o UTC é
 * só um calendário sem mudanças de hora para contar dias entre duas datas civis. O fuso entra
 * **uma vez**, ao decidir que dia civil é hoje.
 */
export function diaDoAnoEmPortugal(ano, quando = new Date()) {
    return diaDaJanelaEmPortugal(janelaDoAno(ano), quando);
}
/**
 * 🔴 D-101 · O índice do dia de hoje em Portugal na JANELA do mapa (a mesma conta, com a origem
 * da fita). Fora da janela sai negativo ou ≥ `nDias` — ver `dentroDaJanela`.
 */
export function diaDaJanelaEmPortugal(j, quando = new Date()) {
    return indiceDaData(j, hojeEmPortugal(quando));
}
//# sourceMappingURL=dia-em-portugal.js.map